import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, Tv, Film, Users, Share2, Check, Mic, Search, Star, Trash2, GripVertical, MoreVertical, ArrowUp, ArrowDown, Loader2, ChevronDown, ListOrdered, Info, Pencil, X, Eye, ThumbsUp, Meh, ThumbsDown, Calendar, Headphones, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AppHeader } from '../components/AppHeader';
import { getPodcastDetails } from '../services/mediaService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sortable Podcast Item
const SortablePodcastItem = ({ podcast, onRemove, onViewDetails, onMarkListened }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: podcast.podcast_id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-4 bg-white rounded-xl border shadow-sm transition-all ${
        isDragging ? 'border-[#F87171] shadow-lg' : 'border-slate-100 hover:border-[#F87171]/30'
      }`}
    >
      {/* Drag Handle */}
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-slate-100">
        <GripVertical className="w-5 h-5 text-slate-400" />
      </div>
      
      {/* Rank Number */}
      <span className="font-outfit font-bold text-xl text-[#3D4F6F] w-8 text-center">
        {podcast.rank}
      </span>
      
      {/* Poster */}
      <div 
        className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100 cursor-pointer hover:scale-105 transition-transform"
        onClick={() => onViewDetails(podcast)}
      >
        {(podcast.image || podcast.thumbnail) ? (
          <img src={podcast.image || podcast.thumbnail} alt={podcast.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Mic className="w-6 h-6 text-slate-300" />
          </div>
        )}
      </div>
      
      {/* Info */}
      <div 
        className="flex-1 min-w-0 cursor-pointer"
        onClick={() => onViewDetails(podcast)}
      >
        <h3 className="font-semibold text-[#3D4F6F] hover:text-[#F87171] transition-colors truncate">{podcast.title}</h3>
        <p className="text-sm text-[#64748B] truncate">{podcast.publisher}</p>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="text-[#3D4F6F] hover:text-[#F87171]"
          onClick={() => onViewDetails(podcast)}
        >
          <Info className="w-4 h-4" />
        </Button>
        <Button
          onClick={() => onMarkListened(podcast)}
          className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full text-sm"
          data-testid={`mark-listened-btn-${podcast.podcast_id}`}
        >
          <Check className="w-4 h-4 mr-1" />
          Listened
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="text-slate-400 hover:text-red-500"
          onClick={() => onRemove(podcast.podcast_id)}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Sortable Ranking Item for the ranking dialog
const SortableRankingItem = ({ podcast, isHighlighted }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: podcast.podcast_id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
        isHighlighted 
          ? 'border-[#F87171] bg-[#F87171]/5 shadow-md' 
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className={`w-5 h-5 ${isHighlighted ? 'text-[#F87171]' : 'text-slate-300'}`} />
      </div>
      
      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
        {podcast.image ? (
          <img src={podcast.image} alt={podcast.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Mic className="w-5 h-5 text-slate-300" />
          </div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <h3 className={`font-medium truncate ${isHighlighted ? 'text-[#F87171]' : 'text-[#3D4F6F]'}`}>
          {podcast.title}
        </h3>
        <p className="text-xs text-slate-500 truncate">{podcast.publisher}</p>
      </div>
      
      {isHighlighted && (
        <span className="px-2 py-1 bg-[#F87171] text-white text-xs rounded-full font-medium">New</span>
      )}
    </div>
  );
};

export default function PodcastListDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [watchlist, setWatchlist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Title editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  
  // Rank new podcast state
  const [isRankDialogOpen, setIsRankDialogOpen] = useState(false);
  const [newlyAddedPodcast, setNewlyAddedPodcast] = useState(null);
  const [rankingOrder, setRankingOrder] = useState([]);
  
  // Info dialog state
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);
  const [selectedPodcast, setSelectedPodcast] = useState(null);
  const [podcastDetails, setPodcastDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Mark as listened state
  const [isListenedDialogOpen, setIsListenedDialogOpen] = useState(false);
  const [listenedPodcast, setListenedPodcast] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [isFirstListen, setIsFirstListen] = useState(true);
  const [listenDate, setListenDate] = useState(new Date().toISOString().split('T')[0]);
  const [listenedComment, setListenedComment] = useState('');
  const [savingListened, setSavingListened] = useState(false);
  const [comparisonStep, setComparisonStep] = useState(false);
  const [existingItems, setExistingItems] = useState([]);
  const [comparisonItem, setComparisonItem] = useState(null);
  const [searchLow, setSearchLow] = useState(0);
  const [searchHigh, setSearchHigh] = useState(0);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    fetchWatchlist();
  }, [id]);

  const fetchWatchlist = async () => {
    try {
      const response = await axios.get(`${API}/podcast-lists/${id}`, { withCredentials: true });
      setWatchlist(response.data);
    } catch (error) {
      toast.error('Failed to load list');
      navigate('/podcasts');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEditTitle = () => {
    setEditTitle(watchlist.title);
    setIsEditingTitle(true);
  };

  const handleCancelEditTitle = () => {
    setIsEditingTitle(false);
    setEditTitle('');
  };

  const handleSaveTitle = async () => {
    if (!editTitle.trim() || editTitle === watchlist.title) {
      setIsEditingTitle(false);
      return;
    }
    try {
      const response = await axios.put(`${API}/podcast-lists/${id}`, { title: editTitle.trim() }, { withCredentials: true });
      setWatchlist(response.data);
      setIsEditingTitle(false);
      toast.success('List renamed!');
    } catch (error) {
      toast.error('Failed to rename list');
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/podcasts/search?query=${encodeURIComponent(searchQuery)}`, { withCredentials: true });
      setSearchResults(response.data.results || []);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  // Add and Rank - adds podcast and opens ranking dialog
  const handleAddAndRank = async (podcast) => {
    try {
      const response = await axios.post(`${API}/podcast-lists/${id}/podcasts`, {
        podcast_id: podcast.podcast_id,
        title: podcast.title,
        publisher: podcast.publisher,
        image: podcast.image,
        thumbnail: podcast.thumbnail,
        description: podcast.description,
        total_episodes: podcast.total_episodes,
        genres: podcast.genres || [],
        position: 'bottom'
      }, { withCredentials: true });
      
      setWatchlist(response.data);
      setIsSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
      
      // If there are multiple podcasts, open ranking dialog
      if (response.data.podcasts && response.data.podcasts.length > 1) {
        setNewlyAddedPodcast(podcast);
        setRankingOrder([...response.data.podcasts]);
        setIsRankDialogOpen(true);
      } else {
        toast.success(`Added "${podcast.title}"!`);
      }
    } catch (error) {
      if (error.response?.data?.detail === 'Already in list') {
        toast.error('Already in this list');
      } else {
        toast.error('Failed to add');
      }
    }
  };

  // Quick Add - adds to top or bottom without ranking
  const handleQuickAdd = async (podcast, position) => {
    try {
      const response = await axios.post(`${API}/podcast-lists/${id}/podcasts`, {
        podcast_id: podcast.podcast_id,
        title: podcast.title,
        publisher: podcast.publisher,
        image: podcast.image,
        thumbnail: podcast.thumbnail,
        description: podcast.description,
        total_episodes: podcast.total_episodes,
        genres: podcast.genres || [],
        position: position
      }, { withCredentials: true });
      
      setWatchlist(response.data);
      setIsSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
      
      const positionText = position === 'top' ? 'at #1' : 'at the bottom';
      toast.success(`Added "${podcast.title}" ${positionText}!`);
    } catch (error) {
      if (error.response?.data?.detail === 'Already in list') {
        toast.error('Already in this list');
      } else {
        toast.error('Failed to add');
      }
    }
  };

  const handleSaveRankingOrder = async () => {
    try {
      const response = await axios.put(`${API}/podcast-lists/${id}/reorder`, {
        podcast_order: rankingOrder.map(p => p.podcast_id)
      }, { withCredentials: true });
      
      setWatchlist(response.data);
      setIsRankDialogOpen(false);
      setNewlyAddedPodcast(null);
      toast.success(`"${newlyAddedPodcast?.title}" added and ranked!`);
    } catch (error) {
      toast.error('Failed to save order');
    }
  };

  const handleRankingDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const oldIndex = rankingOrder.findIndex(p => p.podcast_id === active.id);
      const newIndex = rankingOrder.findIndex(p => p.podcast_id === over.id);
      setRankingOrder(arrayMove(rankingOrder, oldIndex, newIndex));
    }
  };

  const handleRemovePodcast = async (podcastId, silent = false) => {
    try {
      const response = await axios.delete(`${API}/podcast-lists/${id}/podcasts/${podcastId}`, { withCredentials: true });
      setWatchlist(response.data);
      if (!silent) {
        toast.success('Removed from list');
      }
    } catch (error) {
      if (!silent) {
        toast.error('Failed to remove');
      }
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const podcasts = watchlist.podcasts || [];
      const oldIndex = podcasts.findIndex(p => p.podcast_id === active.id);
      const newIndex = podcasts.findIndex(p => p.podcast_id === over.id);
      
      const newOrder = arrayMove(podcasts, oldIndex, newIndex);
      
      // Optimistic update
      setWatchlist({ ...watchlist, podcasts: newOrder.map((p, i) => ({ ...p, rank: i + 1 })) });
      
      try {
        await axios.put(`${API}/podcast-lists/${id}/reorder`, {
          podcast_order: newOrder.map(p => p.podcast_id)
        }, { withCredentials: true });
      } catch (error) {
        toast.error('Failed to reorder');
        fetchWatchlist();
      }
    }
  };

  const handleCopyShareLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/join/podcast/${watchlist.share_code}`);
    setCopied(true);
    toast.success('Share link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      navigate('/');
    }
  };

  // View podcast details
  const handleViewDetails = async (podcast) => {
    setSelectedPodcast(podcast);
    setIsInfoDialogOpen(true);
    setLoadingDetails(true);
    
    try {
      // Use canonical media API for podcast details
      const details = await getPodcastDetails(podcast.podcast_id);
      setPodcastDetails(details);
    } catch (error) {
      console.error('Canonical podcast details failed, falling back:', error);
      // Fallback to old API
      try {
        const response = await axios.get(`${API}/podcasts/${podcast.podcast_id}`, { withCredentials: true });
        setPodcastDetails(response.data);
      } catch (e) {
        toast.error('Failed to load podcast details');
      }
    } finally {
      setLoadingDetails(false);
    }
  };

  // Open mark as listened dialog
  const handleOpenListenedDialog = (podcast) => {
    setListenedPodcast(podcast);
    setSelectedGrade(null);
    setIsFirstListen(true);
    setListenDate(new Date().toISOString().split('T')[0]);
    setListenedComment('');
    setComparisonStep(false);
    setIsListenedDialogOpen(true);
  };

  const handleGradeSelect = (grade) => {
    setSelectedGrade(grade);
  };

  const handleContinueToRanking = async () => {
    if (!selectedGrade) return;
    setSavingListened(true);
    
    try {
      const response = await axios.get(`${API}/listened-podcasts?grade=${selectedGrade}`, { withCredentials: true });
      const items = response.data.podcasts || [];
      setExistingItems(items);
      
      if (items.length === 0) {
        await saveListened(selectedGrade, 1);
      } else {
        setSearchLow(0);
        setSearchHigh(items.length);
        const midIndex = Math.floor(items.length / 2);
        setComparisonItem(items[midIndex]);
        setComparisonStep(true);
        setSavingListened(false);
      }
    } catch (error) {
      toast.error('Failed to fetch existing items');
      setSavingListened(false);
    }
  };

  const handleComparison = async (preferNew) => {
    let newLow = searchLow;
    let newHigh = searchHigh;
    
    const midIndex = Math.floor((newLow + newHigh) / 2);
    
    if (preferNew) {
      newHigh = midIndex;
    } else {
      newLow = midIndex + 1;
    }
    
    if (newLow >= newHigh) {
      await saveListened(selectedGrade, newLow + 1);
    } else {
      setSearchLow(newLow);
      setSearchHigh(newHigh);
      const nextMidIndex = Math.floor((newLow + newHigh) / 2);
      setComparisonItem(existingItems[nextMidIndex]);
    }
  };

  const saveListened = async (grade, rank) => {
    setSavingListened(true);
    try {
      await axios.post(`${API}/listened-podcasts/direct`, {
        podcast_id: listenedPodcast.podcast_id,
        title: listenedPodcast.title,
        publisher: listenedPodcast.publisher,
        image: listenedPodcast.image || listenedPodcast.thumbnail,
        thumbnail: listenedPodcast.thumbnail || listenedPodcast.image,
        description: listenedPodcast.description,
        total_episodes: listenedPodcast.total_episodes || 0,
        genres: listenedPodcast.genres || [],
        grade: grade,
        rank_in_group: rank,
        is_first_listen: isFirstListen,
        listen_date: listenDate,
        comment: listenedComment
      }, { withCredentials: true });
      
      toast.success(`Marked "${listenedPodcast.title}" as listened!`);
      setIsListenedDialogOpen(false);
      
      // Remove from list after marking as listened (silent - no extra toast)
      await handleRemovePodcast(listenedPodcast.podcast_id, true);
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSavingListened(false);
    }
  };

  const grades = [
    { g: 'liked', icon: <ThumbsUp className="w-5 h-5 text-green-500" />, label: 'Liked it', cls: 'border-green-400 bg-green-50 text-green-700' },
    { g: 'alright', icon: <Meh className="w-5 h-5 text-amber-500" />, label: 'It was alright', cls: 'border-amber-400 bg-amber-50 text-amber-700' },
    { g: 'disliked', icon: <ThumbsDown className="w-5 h-5 text-red-400" />, label: "Didn't like it", cls: 'border-red-400 bg-red-50 text-red-700' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#3D4F6F] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F87171] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const isJointList = watchlist?.list_type === 'joint';
  const podcasts = watchlist?.podcasts || [];

  return (
    <div className="min-h-screen bg-[#3D4F6F]">
      {/* Header with full navigation */}
      <AppHeader user={user} showBack={true} backPath="/podcasts" />

      <main className="container mx-auto px-6 py-8">
        {/* Title with edit */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-2">
                {isEditingTitle ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveTitle();
                        if (e.key === 'Escape') handleCancelEditTitle();
                      }}
                      className="text-lg font-bold bg-white/90 text-[#3D4F6F] max-w-[250px]"
                      autoFocus
                      data-testid="edit-title-input"
                    />
                    <Button size="sm" onClick={handleSaveTitle} className="bg-white/90 text-[#F87171] hover:bg-white rounded-full" data-testid="save-title-btn">
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={handleCancelEditTitle} className="text-white hover:bg-white/10 rounded-full" data-testid="cancel-edit-btn">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <h1 className="font-outfit text-xl sm:text-2xl font-bold text-white">{watchlist?.title}</h1>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={handleStartEditTitle}
                      className="text-white/60 hover:text-white hover:bg-white/10 rounded-full"
                      data-testid="edit-title-btn"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </>
                )}
                {isJointList && !isEditingTitle && <Users className="w-5 h-5 text-white/70" />}
              </div>
              <p className="text-white/70 text-sm">{podcasts.length} podcasts</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isJointList && (
              <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20" onClick={handleCopyShareLink}>
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
                {copied ? 'Copied!' : 'Share'}
              </Button>
            )}
            <Button className="bg-white text-[#F87171] hover:bg-white/90" onClick={() => setIsSearchOpen(true)} data-testid="add-podcast-btn">
              <Plus className="w-4 h-4 mr-2" />Add Podcast
            </Button>
          </div>
        </div>

        {/* Podcast List */}
        <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
          <CardContent className="p-6">
            {podcasts.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Mic className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium mb-2">No podcasts yet</p>
                <p className="text-sm mb-4">Start adding podcasts to your list!</p>
                <Button className="bg-[#F87171] hover:bg-[#EF4444] text-white" onClick={() => setIsSearchOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />Add Your First Podcast
                </Button>
              </div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={podcasts.map(p => p.podcast_id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {podcasts.map((podcast) => (
                      <SortablePodcastItem 
                        key={podcast.podcast_id} 
                        podcast={podcast} 
                        onRemove={handleRemovePodcast}
                        onViewDetails={handleViewDetails}
                        onMarkListened={handleOpenListenedDialog}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Search Dialog with Add Options */}
      <Dialog open={isSearchOpen} onOpenChange={setIsSearchOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="search-podcast-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3D4F6F]">Add Podcast</DialogTitle>
            <DialogDescription>Search for podcasts to add to your list</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSearch} className="flex gap-2 mb-4">
            <Input
              placeholder="Search podcasts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1"
              data-testid="search-podcast-input"
            />
            <Button type="submit" className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full" disabled={searching}>
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </form>
          
          <ScrollArea className="h-[400px]">
            {searchResults.length > 0 ? (
              <div className="space-y-3">
                {searchResults.map((podcast, index) => (
                  <div key={podcast.podcast_id} className="flex gap-4 p-3 rounded-xl hover:bg-slate-50 group" data-testid={`search-result-${index}`}>
                    {podcast.image ? (
                      <img src={podcast.image} alt={podcast.title} className="w-16 h-16 object-cover rounded-lg" />
                    ) : (
                      <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center">
                        <Mic className="w-6 h-6 text-slate-400" />
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-[#3D4F6F]">{podcast.title}</h4>
                      <p className="text-sm text-[#64748B]">{podcast.publisher}</p>
                      <div className="flex items-center gap-2 text-sm text-[#64748B] mt-1">
                        {podcast.listen_score && typeof podcast.listen_score === 'number' && (
                          <span className="flex items-center gap-1">
                            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                            {podcast.listen_score}
                          </span>
                        )}
                        <span>{podcast.total_episodes} episodes</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2 justify-center">
                      {/* Add Dropdown with options */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" className="rounded-full bg-[#F87171] hover:bg-[#EF4444] text-white" data-testid={`add-podcast-btn-${index}`}>
                            <Plus className="w-4 h-4 mr-1" />Add
                            <ChevronDown className="w-3 h-3 ml-1" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem onClick={() => handleQuickAdd(podcast, 'top')} className="cursor-pointer" data-testid={`quick-add-top-${index}`}>
                            <ArrowUp className="w-4 h-4 mr-2 text-green-600" />Add to Top (#1)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleQuickAdd(podcast, 'bottom')} className="cursor-pointer" data-testid={`quick-add-bottom-${index}`}>
                            <ArrowDown className="w-4 h-4 mr-2 text-blue-600" />Add to Bottom
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleAddAndRank(podcast)} className="cursor-pointer" data-testid={`add-and-rank-${index}`}>
                            <ListOrdered className="w-4 h-4 mr-2 text-[#F87171]" />Add & Rank...
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            ) : searchQuery && !searching ? (
              <div className="text-center py-8 text-[#64748B]">No podcasts found</div>
            ) : (
              <div className="text-center py-8 text-[#64748B]">Search for podcasts to add</div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Rank New Podcast Dialog */}
      <Dialog open={isRankDialogOpen} onOpenChange={(open) => { if (!open) { setIsRankDialogOpen(false); setNewlyAddedPodcast(null); } }}>
        <DialogContent className="max-w-lg max-h-[80vh]" data-testid="rank-podcast-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3D4F6F]">Rank Your Priority</DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Drag <span className="font-semibold text-[#F87171]">{newlyAddedPodcast?.title}</span> to set your listening priority.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 max-h-[400px] overflow-y-auto">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleRankingDragEnd}>
              <SortableContext items={rankingOrder.map(p => p.podcast_id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {rankingOrder.map((podcast) => (
                    <SortableRankingItem 
                      key={podcast.podcast_id} 
                      podcast={podcast} 
                      isHighlighted={podcast.podcast_id === newlyAddedPodcast?.podcast_id}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => { setIsRankDialogOpen(false); setNewlyAddedPodcast(null); toast.success(`Added "${newlyAddedPodcast?.title}"!`); }} className="rounded-full">
              Skip
            </Button>
            <Button onClick={handleSaveRankingOrder} className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full" data-testid="save-rank-btn">
              <Check className="w-4 h-4 mr-2" />Save Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Podcast Info Dialog */}
      <Dialog open={isInfoDialogOpen} onOpenChange={setIsInfoDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3D4F6F]">Podcast Details</DialogTitle>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="py-8 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#F87171]" />
            </div>
          ) : selectedPodcast && (
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                  {selectedPodcast.image ? (
                    <img src={selectedPodcast.image} alt={selectedPodcast.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Mic className="w-10 h-10 text-slate-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-outfit font-semibold text-lg text-[#3D4F6F]">{selectedPodcast.title}</h3>
                  <p className="text-sm text-[#64748B]">{selectedPodcast.publisher}</p>
                  {podcastDetails?.total_episodes && (
                    <p className="text-xs text-slate-500 mt-1">{podcastDetails.total_episodes} episodes</p>
                  )}
                </div>
              </div>
              
              {(podcastDetails?.description || selectedPodcast.description) && (
                <div>
                  <h4 className="text-sm font-medium text-[#3D4F6F] mb-1">Description</h4>
                  <p className="text-sm text-[#64748B] whitespace-pre-line">
                    {podcastDetails?.description || selectedPodcast.description}
                  </p>
                </div>
              )}
              
              {selectedPodcast.genres?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-[#3D4F6F] mb-2">Genres</h4>
                  <div className="flex flex-wrap gap-1">
                    {selectedPodcast.genres.map((genre, i) => (
                      <span key={typeof genre === 'object' ? genre.id || genre.name : `genre-${genre}-${i}`} className="px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs">
                        {typeof genre === 'object' ? genre.name : genre}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => { setIsInfoDialogOpen(false); handleOpenListenedDialog(selectedPodcast); }}
                  className="flex-1 bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
                >
                  <Eye className="w-4 h-4 mr-2" />Mark as Listened
                </Button>
                {podcastDetails?.website && (
                  <Button
                    variant="outline"
                    onClick={() => window.open(podcastDetails.website, '_blank')}
                    className="rounded-full"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark as Listened Dialog */}
      <Dialog open={isListenedDialogOpen} onOpenChange={setIsListenedDialogOpen}>
        <DialogContent className="max-w-md" data-testid="mark-listened-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3D4F6F]">
              {comparisonStep ? 'Which do you prefer?' : 'Mark as Listened'}
            </DialogTitle>
            {!comparisonStep && (
              <DialogDescription className="text-[#64748B]">
                How did you like "{listenedPodcast?.title}"?
              </DialogDescription>
            )}
          </DialogHeader>
          
          {!comparisonStep ? (
            <div className="space-y-4 py-4">
              {/* Grade Selection */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-[#64748B] mb-2">How did you like it?</p>
                {grades.map(({ g, icon, label, cls }) => (
                  <button 
                    key={g} 
                    data-testid={`grade-${g}-btn`} 
                    onClick={() => handleGradeSelect(g)} 
                    className={`w-full flex items-center gap-4 p-3 rounded-xl border-2 transition-all ${
                      selectedGrade === g ? cls : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className={selectedGrade === g ? '' : 'opacity-50'}>{icon}</div>
                    <span className={`font-medium ${selectedGrade === g ? '' : 'text-slate-500'}`}>{label}</span>
                    {selectedGrade === g && <Check className="w-5 h-5 ml-auto" />}
                  </button>
                ))}
              </div>
              
              {/* First Listen Toggle */}
              <div className="pt-2 border-t border-slate-100">
                <button
                  onClick={() => setIsFirstListen(!isFirstListen)}
                  className={`w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                    isFirstListen ? 'border-purple-400 bg-purple-50 text-purple-700' : 'border-slate-200 hover:border-slate-300 text-[#64748B]'
                  }`}
                >
                  <Headphones className={`w-5 h-5 ${isFirstListen ? 'text-purple-600' : 'text-slate-400'}`} />
                  <span className="font-medium text-sm">First time listening</span>
                </button>
              </div>
              
              {/* Listen Date */}
              <div className="pt-2">
                <label className="text-sm font-medium text-[#64748B] mb-2 block flex items-center gap-2">
                  <Calendar className="w-4 h-4" />Date completed
                </label>
                <Input type="date" value={listenDate} onChange={(e) => setListenDate(e.target.value)} className="w-full" />
              </div>
              
              {/* Comments */}
              <div className="pt-2">
                <label className="text-sm font-medium text-[#64748B] mb-2 block">Thoughts (optional)</label>
                <Textarea placeholder="What did you think?" value={listenedComment} onChange={(e) => setListenedComment(e.target.value)} className="resize-none h-20" />
              </div>
              
              {/* Continue Button */}
              <Button
                onClick={handleContinueToRanking}
                disabled={!selectedGrade || savingListened}
                className="w-full bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full mt-2"
              >
                {savingListened ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowUp className="w-4 h-4 mr-2" />}
                Continue to Ranking
              </Button>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <p className="text-sm text-[#64748B] text-center mb-4">
                Tap the one you prefer to rank "{listenedPodcast?.title}"
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {/* New podcast */}
                <button
                  onClick={() => handleComparison(true)}
                  className="p-4 border-2 border-slate-200 rounded-xl hover:border-[#F87171] hover:bg-[#F87171]/5 transition-all text-center"
                >
                  <div className="w-16 h-16 rounded-lg overflow-hidden mx-auto mb-2 bg-slate-100">
                    {listenedPodcast?.image ? (
                      <img src={listenedPodcast.image} alt={listenedPodcast.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Mic className="w-8 h-8 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <p className="text-sm font-medium text-[#3D4F6F] truncate">{listenedPodcast?.title}</p>
                  <p className="text-xs text-[#F87171]">New</p>
                </button>
                
                {/* Comparison item */}
                <button
                  onClick={() => handleComparison(false)}
                  className="p-4 border-2 border-slate-200 rounded-xl hover:border-[#F87171] hover:bg-[#F87171]/5 transition-all text-center"
                >
                  <div className="w-16 h-16 rounded-lg overflow-hidden mx-auto mb-2 bg-slate-100">
                    {comparisonItem?.thumbnail ? (
                      <img src={comparisonItem.thumbnail} alt={comparisonItem.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Mic className="w-8 h-8 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <p className="text-sm font-medium text-[#3D4F6F] truncate">{comparisonItem?.title}</p>
                  <p className="text-xs text-slate-500">#{comparisonItem?.rank_in_group}</p>
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
